import { useUser } from '@/Common/util/hooks/useUser'
import { router } from '@inertiajs/vue3'
import * as Sentry from '@sentry/vue'
import { watch } from 'vue'

function inertiaRoutingInstrumentation(
  customStartTransaction,
  startTransactionOnPageLoad = true,
  startTransactionOnLocationChange = true
) {
  let activeTransaction
  let name
  if (startTransactionOnPageLoad) {
    name = route().current()
    activeTransaction = customStartTransaction({
      name,
      op: 'pageload',
      metadata: {
        source: 'route',
      },
    })
  }

  if (startTransactionOnLocationChange) {
    router.on('before', (_to, _from) => {
      if (activeTransaction) {
        activeTransaction.finish()
      }

      const newName = route().current()

      if (newName !== name) {
        activeTransaction = customStartTransaction({
          name: newName,
          op: 'navigation',
          metadata: {
            source: 'route',
          },
        })
      }
    })

    router.on('finish', () => {
      activeTransaction.setName(route().current(), 'route')
    })
  }
}

export const sentryPlugin = {
  install(app) {
    if (
      import.meta.env.VITE_SENTRY_ENVIRONMENT === 'local' ||
      import.meta.env.VITE_SENTRY_ENVIRONMENT === 'testing'
    )
      return

    Sentry.init({
      app,
      dsn: 'https://50ca443eec5b1d0045d9a9ec7b5ec8b2@o4504172147638272.ingest.sentry.io/4506617149325312',
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
      integrations: [
        new Sentry.BrowserTracing({
          routingInstrumentation: inertiaRoutingInstrumentation,
        }),
        new Sentry.Replay({
          maskAllText: false,
          maskAllInputs: false,
          blockAllMedia: false,
          // Capture full request/response bodies and headers for our own
          // backend traffic (Inertia visits, XHR, fetch) so they show up in
          // the replay's network tab. Same-origin only, so third-party
          // requests are left out.
          networkDetailAllowUrls: [window.location.origin],
          networkCaptureBodies: true,
          networkRequestHeaders: ['X-Inertia', 'X-Inertia-Version'],
          networkResponseHeaders: ['X-Inertia'],
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: 1.0,

      // Session Replay
      replaysSessionSampleRate: 1,
      replaysOnErrorSampleRate: 1,
    })

    watch(
      () => useUser.value?.id,
      () => {
        Sentry.setUser(
          useUser.value
            ? {
                id: useUser.value.id,
                email: useUser.value.email,
                username: useUser.value.name,
                ip_address: '{{auto}}',
              }
            : null
        )
      },
      { immediate: true }
    )

    router.on('error', (event) => {
      const errors = event.detail.errors ?? {}

      Sentry.withScope((scope) => {
        scope.setLevel('warning')
        scope.setTag('error_type', 'inertia_form_validation')
        scope.setTag('route', route().current() ?? 'unknown')

        // Group every Inertia form error into a single Sentry issue so they
        // can be tracked and counted from one dashboard rather than only
        // appearing as a breadcrumb inside an individual session replay.
        // The originating request/response bodies are captured by Session
        // Replay (see networkDetailAllowUrls above).
        scope.setFingerprint(['inertia-form-errors'])

        scope.setContext('inertia_response', {
          errors,
          fields: Object.keys(errors),
        })

        Sentry.captureMessage('Inertia Form Errors', 'warning')
      })

      // Keep a breadcrumb so the error is still visible on the replay timeline.
      Sentry.addBreadcrumb({
        category: 'inertia',
        message: 'Inertia Form Errors',
        level: 'warning',
        data: { errors: JSON.stringify(errors) },
      })
    })

    // Server errors (5xx). Inertia fires `httpException` for any response with
    // status >= 400, including non-Inertia responses such as gateway/Lambda
    // 502/503s that never reach the Laravel log. We only capture 5xx here so
    // they land in the errors dashboard (and, via replaysOnErrorSampleRate, a
    // replay) instead of only living in the server logs.
    router.on('httpException', (event) => {
      const response = event.detail.response
      const status = response?.status
      if (!status || status < 500) return

      Sentry.withScope((scope) => {
        scope.setLevel('error')
        scope.setTag('error_type', 'server_error')
        scope.setTag('http_status', String(status))
        scope.setTag('route', route().current() ?? 'unknown')

        // One issue per status code, aggregated across all sessions.
        scope.setFingerprint(['server-error', String(status)])

        scope.setContext('server_response', {
          status,
          headers: response.headers,
          body:
            typeof response.data === 'string'
              ? response.data.slice(0, 2000)
              : response.data,
        })

        Sentry.captureException(new Error(`Server error ${status}`))
      })
    })

    // No HTTP response at all (connection reset, hard timeout). Lambda
    // timeouts usually surface as a 503 above, but a dropped connection lands
    // here instead.
    router.on('networkError', (event) => {
      const error = event.detail.error

      Sentry.withScope((scope) => {
        scope.setLevel('error')
        scope.setTag('error_type', 'network_error')
        scope.setTag('route', route().current() ?? 'unknown')
        scope.setFingerprint(['inertia-network-error'])

        Sentry.captureException(
          error instanceof Error ? error : new Error('Inertia network error')
        )
      })
    })
  },
}
